import React, { useState, useRef, useEffect, useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import { API_BASE } from '../api';

function Header({ username, onLogout, onToggleSidebar, isSidebarOpen }) {
    const [showDropdown, setShowDropdown] = useState(false);
    const [showNotifDropdown, setShowNotifDropdown] = useState(false);
    const [showThemeOptions, setShowThemeOptions] = useState(false);
    const [notifications, setNotifications] = useState([]); 
    const [hasPendingTasks, setHasPendingTasks] = useState(false);
    const [isBlinking, setIsBlinking] = useState(false);
    const [userData, setUserData] = useState(null);
    const dropdownRef = useRef(null);
    
    const { currentTheme, toggleTheme, isDark } = useContext(ThemeContext);
    

    // Helper function for header background
    const getHeaderBackground = () => {
        if (isDark) {
            return `linear-gradient(135deg, ${currentTheme.primary}dd, ${currentTheme.secondary}dd)`;
        } else {
            return `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.secondary})`;
        }
    };

    // Function to determine if a task should be shown based on user role
    const shouldShowTask = (item) => {
        if (!item.is_pending_task) return true; // Show general notifications
        
        const userRole = userData?.role || sessionStorage.getItem('userRole');
        const sourceTable = item.source_table;
        
        // For admins/managers - show all tasks
        if (userRole === 'admin' || userRole === 'manager') {
            return true;
        }
        
        // For editors - only show editor-related tasks
        if (userRole === 'editor') {
            return sourceTable === 'tasks' || sourceTable === 'editor_tasks';
        }
        
        // For designers - only show designer-related tasks
        if (userRole === 'designer') {
            return sourceTable === 'tasks' || 
                   sourceTable === 'editor_tasks' || 
                   sourceTable === 'designer_tasks';
        }
        
        // For clients - only show their approval tasks
        if (userRole === 'client' || userRole === 'user') {
            return sourceTable === 'tasks' || sourceTable === 'editor_tasks';
        }
        
        // For operators - only show tasks table tasks
        if (userRole === 'operator') {
            return sourceTable === 'tasks';
        }
        
        return false;
    };

    // Filter for only pending/needs_revision tasks
    const getFilteredTasks = () => {
        return notifications.filter(item => {
            // Skip general notifications (non-tasks)
            if (!item.is_pending_task) return false;
            
            // Get the task status
            const status = (item.task_status || item.status || '').toLowerCase();
            
            // Check if status is pending or needs revision
            const isPendingOrNeedsRevision = 
                status.includes('pending') || 
                status.includes('needs_revision') ||
                status.includes('needs revision') ||
                status.includes('revision') ||
                status === 'todo' ||
                status === 'inprogress' ||
                status === 'in_progress' ||
                status === 'unassigned' ||
                status === 'assigned' ||
                status === 'working';
            
            // Also check if task should be shown based on user role
            const shouldShow = shouldShowTask(item);
            
            return isPendingOrNeedsRevision && shouldShow;
        });
    };

    // Get filtered tasks count
    const filteredTasks = getFilteredTasks();
    
    // Fetch pending tasks and notifications
    useEffect(() => {
        fetchUserData();
        const intervalId = setInterval(fetchNotificationsAndTasks, 30000);
        fetchNotificationsAndTasks(); 
        return () => clearInterval(intervalId);
    }, []);

    // Blinking effect watches the filtered tasks count
    useEffect(() => {
        if (filteredTasks.length > 0) {
            setHasPendingTasks(true);
            setIsBlinking(true);
            const blinkInterval = setInterval(() => setIsBlinking(prev => !prev), 1000);
            return () => clearInterval(blinkInterval);
        } else {
            setHasPendingTasks(false);
            setIsBlinking(false);
        }
    }, [filteredTasks.length]);

    // Fetch notifications and tasks
    const fetchNotificationsAndTasks = async () => {
        const userId = sessionStorage.getItem('userId');
        const userRole = sessionStorage.getItem('userRole');

        if (!userId) {
            console.log('No user ID found in session storage');
            return;
        }
        
        try {
            const response = await fetch(`${API_BASE}/notifications.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    operation: 'get_notifications',
                    user_id: parseInt(userId),
                    user_role: userRole
                })
            });

            if (!response.ok) {
                console.error(`HTTP error! Status: ${response.status}`);
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            const notificationsArray = Array.isArray(data.notifications) ? data.notifications : [];
            
            if (data.success) {
                setNotifications(notificationsArray);
                console.log('Received All Alerts:', notificationsArray.length);
                console.log('Filtered Tasks Count:', getFilteredTasks().length);
                
                // Log task details for debugging
                if (notificationsArray.length > 0) {
                    notificationsArray.forEach((item, index) => {
                        if (item.is_pending_task) {
                            console.log(`Task ${index}:`, {
                                source: item.source_table,
                                status: item.task_status || item.status,
                                video_name: item.video_name,
                                shouldShow: shouldShowTask(item),
                                isPendingOrNeedsRevision: (item.task_status || item.status || '').toLowerCase().includes('pending') || 
                                                          (item.task_status || item.status || '').toLowerCase().includes('revision')
                            });
                        }
                    });
                }
            } else {
                console.error('API operation failed:', data.message);
                setNotifications([]);
            }
        } catch (error) {
            console.error('Error fetching alerts:', error);
            setNotifications([]);
        }
    };

    const markTasksAsAcknowledged = async () => {
        const userId = sessionStorage.getItem('userId');
        const userRole = userData?.role;
        
        if (!userId) return;

        try {
            await fetch(`${API_BASE}/notifications.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    operation: 'mark_all_read',
                    user_id: parseInt(userId),
                    user_role: userRole
                })
            });
            
            // Clear acknowledged system notifications on the client
            setNotifications(prev => prev.filter(n => n.is_pending_task));
            
        } catch (error) {
            console.error('Error acknowledging tasks:', error);
        }
    };

    const toggleNotificationDropdown = () => {
        setShowNotifDropdown(prev => {
            const newState = !prev;
            if (newState && filteredTasks.length > 0) {
                 markTasksAsAcknowledged();
            }
            return newState;
        });
    };

    const fetchUserData = async () => {
        try {
            const userId = sessionStorage.getItem('userId');
            if (!userId) return;

            const response = await fetch(`${API_BASE}/auth.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ operation: 'get_user_profile', user_id: parseInt(userId) })
            });

            const data = await response.json();
            if (data && data.user) {
                setUserData(data.user);
                sessionStorage.setItem('userRole', data.user.role);
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
        }
    };

    const handleSignOut = () => {
        setShowDropdown(false);
        sessionStorage.clear();
        if (onLogout) onLogout();
    };

    const canChangeTheme = userData?.role === 'manager' || userData?.role === 'admin';
    const displayName = userData?.name || userData?.username || username || 'User';

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'No date';
        try {
            return new Date(dateString).toLocaleDateString();
        } catch (error) {
            return 'Invalid date';
        }
    };
    
    // Get badge count - only filtered tasks (pending/needs_revision)
    const badgeCount = filteredTasks.length;
    
    // Function to get icon based on task type
    const getTaskIcon = (item) => {
        if (!item.is_pending_task) return 'fa-bell';
        
        switch (item.source_table) {
            case 'editor_tasks':
                return 'fa-edit';
            case 'designer_tasks':
                return 'fa-paint-brush';
            case 'tasks':
                return 'fa-video';
            default:
                return 'fa-tasks';
        }
    };

    // Function to get icon color based on task type
    const getTaskIconColor = (item) => {
        if (!item.is_pending_task) return '#007bff';
        
        switch (item.source_table) {
            case 'editor_tasks':
                return '#28a745';
            case 'designer_tasks':
                return '#ffc107';
            case 'tasks':
                return currentTheme.primary;
            default:
                return currentTheme.secondary;
        }
    };

    // Function to get status badge color
    const getStatusBadgeColor = (status) => {
        if (!status) return '#dc3545';
        
        const statusLower = status.toLowerCase();
        if (statusLower.includes('revision') || statusLower.includes('needs')) {
            return '#ffc107';
        } else if (statusLower.includes('pending') || statusLower === 'todo') {
            return '#dc3545';
        } else if (statusLower.includes('progress') || statusLower.includes('assigned') || statusLower.includes('working')) {
            return '#17a2b8';
        } else if (statusLower.includes('approved') || statusLower.includes('completed')) {
            return '#28a745';
        } else {
            return '#6c757d';
        }
    };

    // Helper function to get task details from item
    const getTaskDetails = (item) => {
        return {
            taskName: item.video_name || item.task_name || item.title || item.name || 'Unnamed Task',
            status: item.task_status || item.status || 'Pending',
            clientName: item.client_name || item.client,
            operatorName: item.operator_name || item.operator,
            description: item.description || item.message || '',
            dueDate: item.due_date || item.deadline,
            priority: item.priority || 'Normal'
        };
    };

    return (
        <header 
            className="navbar navbar-expand-lg navbar-dark fixed-top shadow-sm"
            style={{ background: getHeaderBackground(), zIndex: 1030, height: '4rem' }}
        >
            <div className="container-fluid">
                {/* Left: Menu, Logo & Name */}
                <div className="d-flex align-items-center">
                    <button 
                        className="btn btn-outline-light border-0 me-3"
                        onClick={onToggleSidebar}
                        aria-label="Toggle sidebar"
                    >
                        <i className={`fas ${isSidebarOpen ? 'fa-times' : 'fa-bars'}`}></i>
                    </button>
                    
                    <div className="d-flex align-items-center">
                        <div className="logo-container me-3">
                            <img 
                                src="https://th.bing.com/th/id/R.eaa07ceb5ef7f04704c7972f3c46c822?rik=JRCQvYhmzQNHWA&pid=ImgRaw&r=0" 
                                alt="Alpha Tracking System" 
                                className="logo-image"
                                style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'contain' }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    const icon = document.createElement('i');
                                    icon.className = 'fas fa-radar-dish text-white';
                                    e.target.parentNode.appendChild(icon);
                                }}
                            />
                        </div>
                        <span className="navbar-brand mb-0 h4 fw-bold text-white">
                            Alpha Tracking System
                        </span>
                    </div>
                </div>

                {/* Right: Theme, Notifications & User */}
                <div className="d-flex align-items-center gap-2" ref={dropdownRef}>
                    
                    {/* Theme Toggle */}
                    <button
                        className="btn btn-outline-light border-0"
                        onClick={toggleTheme}
                        aria-label="Toggle theme"
                        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        style={{ padding: '0.375rem 0.75rem' }}
                    >
                        <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
                    </button>

                    {canChangeTheme && (
                        <div className="dropdown">
                            <button 
                                className="btn btn-outline-light border-0"
                                onClick={() => setShowThemeOptions(!showThemeOptions)}
                                title="More theme options"
                                style={{ padding: '0.375rem 0.75rem' }}
                            >
                                <i className="fas fa-palette"></i>
                            </button>
                        </div>
                    )}

                    {/* Notifications Bell Icon */}
                    <div className="dropdown">
                        <button 
                            className={`btn btn-outline-light position-relative border-0 ${isBlinking ? 'blinking-icon' : ''}`}
                            onClick={toggleNotificationDropdown}
                            title={`Pending Tasks (${badgeCount})`}
                            style={{ 
                                padding: '0.375rem 0.75rem',
                                animation: isBlinking ? 'blink 1s infinite' : 'none'
                            }}
                        >
                            <i className="fas fa-bell"></i>
                            {/* Display the count inside the badge - only filtered tasks */}
                            {badgeCount > 0 && (
                                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-light">
                                    {badgeCount}
                                    <span className="visually-hidden">pending tasks</span>
                                </span>
                            )}
                        </button>

                        {showNotifDropdown && (
                            <div className="dropdown-menu dropdown-menu-end show"
                                style={{ 
                                    minWidth: '400px', 
                                    maxHeight: '500px', 
                                    overflowY: 'auto',
                                    marginRight: '0',
                                    right: '0',
                                    left: 'auto',
                                    padding: '0'
                                }}>
                                <h6 className="dropdown-header text-center py-2 fw-bold" style={{borderBottom: '1px solid #eee'}}>
                                    <i className="fas fa-tasks me-1"></i> Pending Tasks ({badgeCount})
                                    {userData?.role && (
                                        <small className="d-block text-muted mt-1">
                                            Role: {userData.role} | Showing: pending/needs revision tasks
                                        </small>
                                    )}
                                </h6>
                                
                                {filteredTasks.length === 0 ? (
                                    <div className="px-4 py-4 text-muted text-center">
                                        <i className="fas fa-check-circle fa-2x mb-2" style={{color: currentTheme.secondary}}></i>
                                        <div className="small mt-2">No pending tasks</div>
                                        <div className="x-small text-muted mt-1">
                                            {notifications.length === 0 
                                                ? 'All caught up!' 
                                                : 'No pending or needs revision tasks for your role'}
                                        </div>
                                    </div>
                                ) : (
                                    filteredTasks.map((item) => {
                                        const taskDetails = getTaskDetails(item);
                                        
                                        return (
                                            <div 
                                                key={`${item.source_table}_${item.task_id || item.id}`}
                                                className="dropdown-item small py-3 px-3"
                                                style={{ 
                                                    whiteSpace: 'normal', 
                                                    borderBottom: '1px solid #f0f0f0',
                                                    cursor: 'default',
                                                    transition: 'background-color 0.3s ease',
                                                    backgroundColor: '#f8f9fa',
                                                }}
                                            >
                                                <div className="d-flex align-items-start">
                                                    {/* Icon based on task type */}
                                                    <i 
                                                        className={`fas ${getTaskIcon(item)} me-3 mt-1`}
                                                        style={{
                                                            color: getTaskIconColor(item),
                                                            fontSize: '1.1rem'
                                                        }}
                                                    ></i>
                                                    <div style={{flexGrow: 1}}>
                                                        {/* TASK DETAILS */}
                                                        <div className="d-flex justify-content-between align-items-start mb-1">
                                                            <h6 className="mb-0 fw-semibold" style={{color: '#333'}}>
                                                                {taskDetails.taskName}
                                                                <small className="text-muted ms-2">({item.source_table})</small>
                                                            </h6>
                                                            <span 
                                                                className="badge ms-2"
                                                                style={{
                                                                    backgroundColor: getStatusBadgeColor(taskDetails.status),
                                                                    color: '#fff',
                                                                    fontSize: '0.6rem'
                                                                }}
                                                            >
                                                                {taskDetails.status.toUpperCase()}
                                                            </span>
                                                        </div>
                                                        
                                                        <div className="mb-2">
                                                            {taskDetails.clientName && taskDetails.clientName.trim() && (
                                                                <div>
                                                                    <small className="text-muted">
                                                                        <strong>Client:</strong> {taskDetails.clientName}
                                                                    </small>
                                                                </div>
                                                            )}
                                                            
                                                            {taskDetails.operatorName && taskDetails.operatorName.trim() && (
                                                                <div>
                                                                    <small className="text-muted">
                                                                        <strong>Operator:</strong> {taskDetails.operatorName}
                                                                    </small>
                                                                </div>
                                                            )}
                                                            
                                                            {taskDetails.description && taskDetails.description.trim() && (
                                                                <div className="mt-1">
                                                                    <small className="text-muted">
                                                                        {taskDetails.description}
                                                                    </small>
                                                                </div>
                                                            )}
                                                            
                                                            {taskDetails.dueDate && (
                                                                <div>
                                                                    <small className="text-muted">
                                                                        <strong>Due:</strong> {formatDate(taskDetails.dueDate)}
                                                                    </small>
                                                                </div>
                                                            )}
                                                        </div>
                                                        
                                                        <div className="x-small text-muted text-end mt-2">
                                                            <span className="badge" style={{backgroundColor: currentTheme.secondary, color: '#fff'}}>
                                                                Task #{item.task_id || item.id || 'N/A'}
                                                            </span>
                                                            <span className="badge ms-1" style={{backgroundColor: '#6c757d', color: '#fff'}}>
                                                                {item.source_table}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                
                                
                            </div>
                        )}
                    </div>

                    {/* User Info */}
                    <div className="dropdown">
                        <button 
                            className="btn btn-outline-light dropdown-toggle d-flex align-items-center"
                            onClick={() => setShowDropdown(!showDropdown)}
                            title="Account"
                            style={{ padding: '0.375rem 0.75rem', minWidth: 'auto' }}
                        >
                            <div className="user-avatar me-2">
                                {userData?.profile_image ? (
                                    <img 
                                        src={userData.profile_image} 
                                        alt={displayName} 
                                        style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} 
                                    />
                                ) : (
                                    <i className="fas fa-user-circle" style={{ fontSize: '1.5rem' }}></i>
                                )}
                            </div>
                            <span className="d-none d-md-inline">{displayName}</span>
                        </button>

                        {showDropdown && (
                            <div className="dropdown-menu dropdown-menu-end show"
                                style={{ minWidth: '150px', marginRight: '0', right: '0', left: 'auto', padding: '0.5rem 0' }}>
                                <button className="dropdown-item d-flex align-items-center py-2 px-3" onClick={handleSignOut}>
                                    <i className="fas fa-sign-out-alt me-2 text-danger"></i>
                                    <span className="text-danger">Logout</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Blinking animation styles */}
            <style jsx>{`
                @keyframes blink {
                    0% { opacity: 1; }
                    50% { opacity: 0.3; }
                    100% { opacity: 1; }
                }
                .blinking-icon {
                    animation: blink 1s infinite;
                }
            `}</style>
        </header>
    );
}

export default Header;